import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './CorporateGiftingPage.css';
import { useSEO } from '../lib/seo';

const GIFTING_PACKAGES = [
  {
    name: 'Starter Pack',
    price: '₹499',
    unit: 'per unit',
    minQty: '50 units',
    img: 'https://images.unsplash.com/photo-1599598425947-5202edd56bdb?w=500&h=500&fit=crop&auto=format&q=80',
    plants: ['Snake Plant', 'ZZ Plant', 'Pothos Golden'],
    features: ['Custom card message', 'Branded tissue wrap', 'Care guide included'],
    tag: null,
  },
  {
    name: 'Signature Pack',
    price: '₹899',
    unit: 'per unit',
    minQty: '25 units',
    img: 'https://images.unsplash.com/photo-1614594975525-e45190c55d0b?w=500&h=500&fit=crop&auto=format&q=80',
    plants: ['Monstera Deliciosa', 'Peace Lily', 'Bird of Paradise'],
    features: ['Custom card message', 'Branded box packaging', 'Care guide + QR tag', 'Decorative planter'],
    tag: 'Most Popular',
  },
  {
    name: 'Luxury Pack',
    price: '₹1,799',
    unit: 'per unit',
    minQty: '10 units',
    img: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=500&h=500&fit=crop&auto=format&q=80',
    plants: ['Bird of Paradise', 'Fiddle Leaf Fig', 'Alocasia'],
    features: ['Handwritten card', 'Premium logo box', 'Care consultation', 'Premium ceramic planter', 'Free delivery'],
    tag: 'Premium',
  },
];

const WHY_GIFTING = [
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
    title: 'Gifts that last',
    text: 'Unlike fruit baskets and hampers, a plant stays — becoming a lasting reminder of your appreciation.',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 8C8 10 5.9 16.17 3.82 19.34a1 1 0 0 0 1.46 1.32C7.5 19 12 17 17 8z"/>
        <path d="M3.82 19.34C4.5 16 6 13 9 11"/>
      </svg>
    ),
    title: 'Sustainably packed',
    text: 'Eco-friendly packaging, zero plastic wrap, and recycled materials — values that reflect well on your brand.',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8V21M12 8C12 8 8 5 6 3M12 8C12 8 16 5 18 3"/>
      </svg>
    ),
    title: 'Fully branded',
    text: 'Custom cards, logo-printed boxes, and ribbon colours — your identity, wrapped around every gift.',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
    title: 'Delivered to every desk',
    text: 'Bulk delivery to office, home addresses, or event venues across 100+ cities. One invoice, zero hassle.',
  },
];

const OCCASIONS = [
  { label: 'Client Appreciation', img: 'https://res.cloudinary.com/ijkxmtot/image/upload/v1782756028/naturiq/products/mpcwauuenpienbj1eain.jpg' },
  { label: 'Employee Onboarding', img: 'https://res.cloudinary.com/ijkxmtot/image/upload/v1782756591/naturiq/products/qntbrxetfpeda8nvusk6.jpg' },
  { label: 'Festive Hampers', img: 'https://res.cloudinary.com/ijkxmtot/image/upload/v1782756851/naturiq/products/tgmwzlod4h0uz8yhx7p8.jpg' },
  { label: 'Office Events', img: 'https://res.cloudinary.com/ijkxmtot/image/upload/v1782757747/naturiq/products/hiklhgi8mtsvrzb3lduy.jpg' },
  { label: 'Year-End Gifting', img: 'https://res.cloudinary.com/ijkxmtot/image/upload/v1782757914/naturiq/products/xc2ut7zvcniydzu3az0c.jpg' },
  { label: 'Product Launches', img: 'https://res.cloudinary.com/ijkxmtot/image/upload/v1782757379/naturiq/products/vukkr9v0yulurd51e7sk.jpg' },
];

const BRANDS = ['TCS', 'Infosys', 'Razorpay', 'Zomato', 'HDFC', 'Wipro', 'PhonePe', 'CRED'];

const FAQS = [
  {
    q: 'What is the minimum order quantity?',
    a: 'Our minimum order starts at just 10 units for the Luxury Pack, 25 units for the Signature Pack, and 50 units for the Starter Pack. For custom requirements below these quantities, feel free to reach out — we\'ll do our best to accommodate.',
  },
  {
    q: 'Can I customise the packaging with our company logo?',
    a: 'Absolutely. Every pack can be fully branded — logo-printed boxes, custom ribbon colours, branded tissue wrap, and personalised card messages. Just share your brand assets when you fill the enquiry form.',
  },
  {
    q: 'How long does delivery take after the order is confirmed?',
    a: 'Standard bulk orders are dispatched within 3–5 business days of approval. For urgent requirements, expedited turnarounds may be possible — our team will advise during the proposal stage.',
  },
  {
    q: 'Do you deliver across India?',
    a: 'Yes. We deliver to 100+ cities pan-India, including deliveries to multiple office locations or individual home addresses. A single invoice covers the entire order regardless of the number of delivery points.',
  },
  {
    q: 'What happens if a plant arrives damaged?',
    a: 'We stand behind every delivery. If any plants arrive in poor condition, contact us within 48 hours with a photo and we\'ll arrange a replacement or full refund for the affected units — no questions asked.',
  },
  {
    q: 'Can I mix and match plants from different packages?',
    a: 'Yes. Our packages are starting points, not rigid menus. You can swap plants, upgrade planters, add care kits, or build an entirely custom curation. Talk to our gifting specialist for a tailored proposal.',
  },
];

const FORM_INIT = {
  name: '', phone: '', email: '', company: '', occasion: '', customisation: '',
};

export default function CorporateGiftingPage() {
  useSEO({
    title: 'Corporate Gifting — Plants for Offices & Events',
    description: 'Send premium indoor plants as corporate gifts across India. Bulk orders, custom packaging and on-time delivery guaranteed by Naturiq.',
    url: '/corporate-gifting',
  });

  const [openFaq, setOpenFaq] = useState(null);
  const [form, setForm] = useState(FORM_INIT);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  // Strip HTML tags before storing/sending
  const sanitize = (val, maxLen) =>
    (typeof val === 'string' ? val : '').replace(/<[^>]*>/g, '').slice(0, maxLen);

  const VALID_OCCASIONS_SET = new Set([
    'Client Appreciation', 'Employee Onboarding', 'Festive Hampers',
    'Office Events', 'Year-End Gifting', 'Product Launches', 'Other',
  ]);

  const validate = () => {
    const e = {};
    const name = form.name.trim();
    const email = form.email.trim();
    const phone = form.phone.replace(/\s/g, '');
    if (!name) e.name = 'Your name is required';
    else if (name.length < 2) e.name = 'Name must be at least 2 characters';
    else if (name.length > 100) e.name = 'Name is too long';
    if (!email) e.email = 'Work email is required';
    else if (email.length > 254) e.email = 'Email is too long';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Valid email required';
    if (!form.phone.trim()) e.phone = 'Phone number is required';
    else if (!/^\+?[\d\s\-()[\]]{7,20}$/.test(form.phone.trim())) e.phone = 'Enter a valid phone number';
    else if (phone.replace(/\D/g, '').length < 10) e.phone = '10-digit phone required';
    return e;
  };

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors(err => ({ ...err, [e.target.name]: '' }));
  };

  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    if (submitting) return; // prevent double-submit
    const e_ = validate();
    if (Object.keys(e_).length) { setErrors(e_); return; }
    setSubmitting(true);
    setSubmitError('');
    try {
      const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      // Sanitize before sending — strip any HTML tags a user might inject
      const payload = {
        source:         'corporate_gifting',
        name:           sanitize(form.name,          100),
        email:          sanitize(form.email,          254).toLowerCase(),
        mobile:         sanitize(form.phone,           20),
        company:        sanitize(form.company,        200),
        occasion:       VALID_OCCASIONS_SET.has(form.occasion) ? form.occasion : '',
        customisation:  sanitize(form.customisation, 1000),
      };
      const res = await fetch(`${BASE}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let msg = 'Something went wrong. Please try again or call us directly.';
        try { const d = await res.json(); if (d?.error) msg = d.error; } catch (_) {}
        console.error('Corporate gifting form error:', msg);
        setSubmitError(msg);
        return;
      }
      setSubmitted(true);
    } catch (err) {
      console.error('Corporate gifting form error:', err);
      setSubmitError('Something went wrong. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="cg-page">

      {/* ── Hero ── */}
      <section className="cg-hero">
        <img
          src="https://res.cloudinary.com/ijkxmtot/image/upload/v1782755774/naturiq/products/nhjp9f0ocx6bcm7ogu0g.jpg"
          alt=""
          className="cg-hero__bg"
          aria-hidden="true"
        />
        <div className="cg-hero__overlay" />
        <div className="cg-hero__overlay-bottom" />

        <div className="cg-hero__layout">

          {/* Left: headline & actions */}
          <div className="cg-hero__content">
            <p className="cg-hero__eyebrow">
              <span className="cg-hero__eyebrow-bar" />
              Bulk Plant Gifting for Businesses
            </p>
            <h1 className="cg-hero__title">
              Give Gifts That<br />
              Grow With Your<br />
              <em className="cg-hero__title-accent">People.</em>
            </h1>
            <p className="cg-hero__sub">
              Thoughtfully curated plant gifts for corporates — from employee welcome kits
              and client appreciation to festive hampers and milestone celebrations.
              Branded, bulk-ready, and delivered pan-India.
            </p>
            <div className="cg-hero__actions">
              <a href="#enquire" className="cg-btn cg-btn--forest">Get a Free Quote</a>
              <a href="https://wa.me/917696999018" className="cg-btn cg-btn--outline-forest">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                Chat on WhatsApp
              </a>
            </div>
            <div className="cg-hero__trust-checks">
              {[
                'Customised branding on every pack',
                'Bulk orders from 10 units',
                'Delivered across 100+ cities',
              ].map(t => (
                <span className="cg-hero__check" key={t}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* Right: form card */}
          <div className="cg-hero__form-panel" id="enquire">

            {/* Form header */}
            <div className="cg-hero__form-header">
              <h2 className="cg-hero__form-title">Get a custom gifting proposal</h2>
              <p className="cg-hero__form-sub">
                Share your requirements and we'll get back within 24 hours.<br />
                <a href="tel:+917696999018" className="cg-hero__form-phone">Prefer to talk? +91 76969 99018</a>
              </p>
            </div>

            {/* Form body */}
            <div className="cg-hero__form-body">
              {submitted ? (
                <div className="cg-form__success cg-form__success--light">
                  <div className="cg-form__success-icon">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                  </div>
                  <h3 className="cg-form__success-title">We've got your enquiry!</h3>
                  <p className="cg-form__success-text">Our gifting specialist will reach out to <strong>{form.email}</strong> within 24 hours with a custom proposal.</p>
                  <button className="cg-btn cg-btn--forest" style={{ fontSize: '13px', padding: '10px 20px' }} onClick={() => { setForm(FORM_INIT); setSubmitted(false); }}>
                    Submit another enquiry
                  </button>
                </div>
              ) : (
                <form className="cg-form cg-form--card" onSubmit={handleSubmit} noValidate>
              {/* Honeypot — hidden from real users, bots fill it in */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ display: 'none' }}
                onChange={() => {}}
              />

                  {/* Name + Email in a row */}
                  <div className="cg-form__row">
                    <div className="cg-form__field">
                      <label className="cg-form__label">Your name *</label>
                      <input
                        className={`cg-form__input${errors.name ? ' cg-form__input--err' : ''}`}
                        name="name" type="text" placeholder="Priya Mehta"
                        value={form.name} onChange={handleChange}
                      />
                      {errors.name && <span className="cg-form__err">{errors.name}</span>}
                    </div>
                    <div className="cg-form__field">
                      <label className="cg-form__label">Work email *</label>
                      <input
                        className={`cg-form__input${errors.email ? ' cg-form__input--err' : ''}`}
                        name="email" type="email" placeholder="priya@company.com"
                        value={form.email} onChange={handleChange}
                      />
                      {errors.email && <span className="cg-form__err">{errors.email}</span>}
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="cg-form__field">
                    <label className="cg-form__label">Phone number *</label>
                    <div className="cg-form__phone-wrap">
                      <span className="cg-form__phone-prefix">+91</span>
                      <input
                        className={`cg-form__input cg-form__input--phone${errors.phone ? ' cg-form__input--err' : ''}`}
                        name="phone" type="tel" maxLength={20} placeholder="7696999018"
                        value={form.phone} onChange={handleChange}
                      />
                    </div>
                    {errors.phone && <span className="cg-form__err">{errors.phone}</span>}
                  </div>

                  {/* Company */}
                  <div className="cg-form__field">
                    <label className="cg-form__label">Company name</label>
                    <input
                      className="cg-form__input"
                      name="company" type="text" placeholder="e.g. Infosys, Razorpay"
                      value={form.company} onChange={handleChange}
                    />
                  </div>

                  {/* Occasion textarea */}
                  <div className="cg-form__field">
                    <label className="cg-form__label">What's the occasion?</label>
                    <textarea
                      className="cg-form__input cg-form__textarea"
                      name="occasion"
                      placeholder="e.g. Diwali gifting, 500 units, logo branding"
                      rows={3}
                      value={form.occasion} onChange={handleChange}
                    />
                  </div>

                  {submitError && (
                    <p style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: '8px' }}>{submitError}</p>
                  )}
                  <button type="submit" className="cg-btn cg-btn--forest cg-form__submit" disabled={submitting}>
                    {submitting ? 'Sending…' : 'Send My Requirements'}
                    {!submitting && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>}
                  </button>

                  {/* Trust row */}
                  <div className="cg-form__trust">
                    <span className="cg-form__trust-item">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                      100% confidential
                    </span>
                    <span className="cg-form__trust-dot" />
                    <span className="cg-form__trust-item">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      Reply in 24 hrs
                    </span>
                    <span className="cg-form__trust-dot" />
                    <span className="cg-form__trust-item">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      No spam
                    </span>
                  </div>

                </form>
              )}
            </div>
          </div>

        </div>
      </section>

      {/* ── Trusted by ── */}
      <section className="cg-trust">
        <p className="cg-trust__label">Trusted by teams at</p>
        <div className="cg-trust__logos">
          {BRANDS.map(b => (
            <span key={b} className="cg-trust__logo">{b}</span>
          ))}
        </div>
      </section>

      {/* ── Why plant gifting ── */}
      <section className="cg-why">
        <div className="cg-why__inner">
          <div className="cg-why__head">
            <p className="cg-eyebrow">Why plants?</p>
            <h2 className="cg-title">The gift that doesn't end up in a drawer.</h2>
            <p className="cg-sub">Corporate gifting should say something — about your company, your values, and how you treat people. Plants say all three.</p>
          </div>
          <div className="cg-why__grid">
            {WHY_GIFTING.map(item => (
              <div key={item.title} className="cg-why__card">
                <div className="cg-why__icon">{item.icon}</div>
                <h3 className="cg-why__card-title">{item.title}</h3>
                <p className="cg-why__card-text">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Occasions ── */}
      <section className="cg-occasions">
        <div className="cg-occasions__head">
          <p className="cg-eyebrow">Perfect for</p>
          <h2 className="cg-title">Every occasion at work.</h2>
        </div>
        <div className="cg-occasions__grid">
          {OCCASIONS.map(occ => (
            <div key={occ.label} className="cg-occasion">
              <img src={occ.img} alt={occ.label} className="cg-occasion__img" loading="lazy" />
              <div className="cg-occasion__overlay" />
              <span className="cg-occasion__label">{occ.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Packages
      <section className="cg-packages" id="packages">
        <div className="cg-packages__inner">
          <div className="cg-packages__head">
            <p className="cg-eyebrow">Gifting packages</p>
            <h2 className="cg-title">Ready-to-send, or built for you.</h2>
            <p className="cg-sub">Each pack is fully customisable — swapping plants, adding branded inserts, or changing packaging is just a call away.</p>
          </div>
          <div className="cg-packages__row">
            {GIFTING_PACKAGES.map(pkg => (
              <div key={pkg.name} className="cg-pkg">
                <div className="cg-pkg__img-wrap">
                  <img src={pkg.img} alt={pkg.name} className="cg-pkg__img" loading="lazy" />
                </div>
                <div className="cg-pkg__body">
                  <h3 className="cg-pkg__name">{pkg.name}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>  ── */}

      {/* ── How it works ── */}
      <section className="cg-how">
        <div className="cg-how__inner">
          <div className="cg-how__head">
            <p className="cg-eyebrow">How it works</p>
            <h2 className="cg-title">From enquiry to doorstep in 5 days.</h2>
          </div>
          <div className="cg-how__steps">
            {[
              { num: '01', title: 'Fill the form', text: 'Tell us the quantity, occasion, and any branding requirements.' },
              { num: '02', title: 'We confirm & quote', text: 'Our team calls you within 24 hours with pricing and plant options.' },
              { num: '03', title: 'Approve & customise', text: 'Review the sample box, approve the card design and packaging.' },
              { num: '04', title: 'Delivered fresh', text: 'Bulk delivery to office or individual addresses, tracked and guaranteed.' },
            ].map((step, i) => (
              <div key={i} className="cg-step">
                <div className="cg-step__num">{step.num}</div>
                <h3 className="cg-step__title">{step.title}</h3>
                <p className="cg-step__text">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="cg-faq">
        <div className="cg-faq__inner">
          <div className="cg-faq__head">
            <p className="cg-eyebrow">FAQ</p>
            <h2 className="cg-title">Questions we get a lot.</h2>
            <p className="cg-sub">Everything you need to know before placing your first order.</p>
          </div>
          <div className="cg-faq__list">
            {FAQS.map((faq, i) => (
              <div key={i} className="cg-faq__item">
                <button
                  className="cg-faq__question"
                  aria-expanded={openFaq === i}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  {faq.q}
                  <span className="cg-faq__icon">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                  </span>
                </button>
                <div className={`cg-faq__answer${openFaq === i ? ' cg-faq__answer--open' : ''}`}>
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


    </main>
  );
}